import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Fade,
  Zoom
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import GetAppIcon from '@mui/icons-material/GetApp';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlaceIcon from '@mui/icons-material/Place';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import { getInventory } from '../services/inventoryService';
import * as XLSX from 'xlsx';

const Inventory = () => {
  const [inventory, setInventory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'compact'

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await getInventory();
      if (response.success) {
        setInventory(response.data);
      } else {
        setError('Ошибка при загрузке остатков товаров');
      }
    } catch (err) {
      setError(err.message || 'Ошибка при загрузке остатков товаров');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    // Заголовки
    worksheetData.push(['Город', 'Район', 'Товар', 'Фасовка', 'Вес', 'Количество', 'Цена за шт.', 'Общая сумма']);

    // Данные
    Object.keys(inventory).forEach(cityName => {
      const city = inventory[cityName];
      
      Object.keys(city.districts).forEach(districtName => {
        const district = city.districts[districtName];
        
        Object.keys(district.products).forEach(productName => {
          const product = district.products[productName];
          
          product.items.forEach(item => {
            worksheetData.push([
              cityName,
              districtName,
              productName,
              item.packaging,
              item.volume,
              item.count,
              item.price,
              item.totalSum
            ]);
          });
        });
      });
    });

    // Итоговая строка
    const totalSum = Object.values(inventory).reduce((sum, city) => sum + city.totalSum, 0);
    const totalCount = Object.values(inventory).reduce((sum, city) => {
      return sum + Object.values(city.districts).reduce((districtSum, district) => {
        return districtSum + Object.values(district.products).reduce((productSum, product) => {
          return productSum + product.totalCount;
        }, 0);
      }, 0);
    }, 0);

    worksheetData.push(['', '', '', '', '', totalCount, '', totalSum]);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Настройка ширины колонок
    worksheet['!cols'] = [
      { width: 15 }, // Город
      { width: 20 }, // Район
      { width: 20 }, // Товар
      { width: 15 }, // Фасовка
      { width: 10 }, // Вес
      { width: 12 }, // Количество
      { width: 12 }, // Цена за шт.
      { width: 15 }  // Общая сумма
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Остатки товаров');

    const currentDate = new Date().toLocaleDateString('ru-RU');
    XLSX.writeFile(workbook, `Остатки_товаров_${currentDate}.xlsx`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const hasData = Object.keys(inventory).length > 0;

  // Подсчет общей статистики
  const totalStats = Object.values(inventory).reduce(
    (acc, city) => {
      const cityItems = Object.values(city.districts).reduce((sum, district) => {
        return sum + Object.values(district.products).reduce((productSum, product) => {
          return productSum + product.totalCount;
        }, 0);
      }, 0);
      return {
        totalValue: acc.totalValue + city.totalSum,
        totalItems: acc.totalItems + cityItems,
        totalCities: acc.totalCities + 1
      };
    },
    { totalValue: 0, totalItems: 0, totalCities: 0 }
  );

  return (
    <Box p={2}>
      {/* Заголовок с переключателем вида */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <InventoryIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Остатки товаров
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalStats.totalCities} городов • {totalStats.totalItems} товаров • {totalStats.totalValue.toLocaleString('ru-RU')} ₽
            </Typography>
          </Box>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Tooltip title="Переключить вид">
            <IconButton 
              onClick={() => setViewMode(viewMode === 'cards' ? 'compact' : 'cards')}
              color={viewMode === 'cards' ? 'primary' : 'default'}
            >
              {viewMode === 'cards' ? <ViewModuleIcon /> : <ViewListIcon />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={exportToExcel}
            disabled={!hasData}
          >
            Экспорт
          </Button>
        </Stack>
      </Box>

      {!hasData ? (
        <Fade in={!hasData}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>Нет данных для отображения</Alert>
        </Fade>
      ) : (
        <Fade in={hasData}>
          <Box>
            {viewMode === 'cards' ? (
              /* Карточный вид */
              <Grid container spacing={3}>
                {Object.keys(inventory).map((cityName, cityIndex) => {
                  const city = inventory[cityName];
                  const totalItems = Object.values(city.districts).reduce((sum, district) => {
                    return sum + Object.values(district.products).reduce((productSum, product) => {
                      return productSum + product.totalCount;
                    }, 0);
                  }, 0);
                  
                  return (
                    <Grid item xs={12} key={cityName}>
                      <Zoom in={true} style={{ transitionDelay: `${cityIndex * 100}ms` }}>
                        <Card 
                          elevation={3} 
                          sx={{ 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            mb: 2
                          }}
                        >
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={2}>
                              <Box display="flex" alignItems="center" minWidth={0} flex={1}>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.2)', 
                                    mr: 2, 
                                    width: 56, 
                                    height: 56,
                                    flexShrink: 0
                                  }}
                                >
                                  <LocationCityIcon fontSize="large" />
                                </Avatar>
                                <Box minWidth={0} flex={1}>
                                  <Typography 
                                    variant="h5" 
                                    fontWeight="bold"
                                    sx={{ 
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {cityName}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    {Object.keys(city.districts).length} районов
                                  </Typography>
                                </Box>
                              </Box>
                              <Box textAlign="right" flexShrink={0}>
                                <Typography variant="h4" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                                  {city.totalSum.toLocaleString('ru-RU')} ₽
                                </Typography>
                                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                                  <Badge badgeContent={totalItems} color="secondary" max={999}>
                                    <Chip 
                                      icon={<ShoppingBagIcon />}
                                      label="товаров"
                                      size="small"
                                      sx={{ 
                                        bgcolor: 'rgba(255,255,255,0.2)', 
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </Badge>
                                </Box>
                              </Box>
                            </Box>
                            
                            {/* Районы в городе */}
                            <Grid container spacing={2}>
                              {Object.keys(city.districts).map(districtName => {
                                const district = city.districts[districtName];
                                const districtItems = Object.values(district.products).reduce((sum, product) => {
                                  return sum + product.totalCount;
                                }, 0);
                                
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={districtName}>
                                    <Paper 
                                      sx={{ 
                                        p: 2, 
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: 2,
                                        border: '1px solid rgba(255,255,255,0.2)'
                                      }}
                                    >
                                      <Box display="flex" alignItems="center" mb={1}>
                                        <PlaceIcon sx={{ mr: 1, fontSize: 18, flexShrink: 0 }} />
                                        <Typography 
                                          variant="subtitle1" 
                                          fontWeight="medium"
                                          sx={{ 
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {districtName}
                                        </Typography>
                                      </Box>
                                      
                                      <Box mb={1}>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                          Товаров: {districtItems}
                                        </Typography>
                                        <LinearProgress 
                                          variant="determinate" 
                                          value={(district.totalSum / city.totalSum) * 100}
                                          sx={{ 
                                            mt: 0.5,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            '& .MuiLinearProgress-bar': {
                                              bgcolor: 'rgba(255,255,255,0.8)'
                                            }
                                          }}
                                        />
                                      </Box>
                                      
                                      <Typography variant="h6" fontWeight="bold">
                                        {district.totalSum.toLocaleString('ru-RU')} ₽
                                      </Typography>
                                      
                                      {/* Товары в районе */}
                                      <Stack spacing={1} mt={2}>
                                        {Object.keys(district.products).map(productName => {
                                          const product = district.products[productName];
                                          return (
                                            <Box key={productName}>
                                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} gap={1}>
                                                <Typography 
                                                  variant="caption" 
                                                  fontWeight="bold" 
                                                  sx={{ 
                                                    opacity: 0.9,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: 0,
                                                    flex: 1
                                                  }}
                                                >
                                                  {productName}
                                                </Typography>
                                                <Typography 
                                                  variant="caption" 
                                                  fontWeight="bold"
                                                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                                >
                                                  {product.totalCount} шт.
                                                </Typography>
                                              </Box>
                                              
                                              {/* Детали фасовок */}
                                              <Stack spacing={0.3}>
                                                {product.items.map((item, index) => (
                                                  <Box 
                                                    key={index}
                                                    sx={{ 
                                                      p: 0.5,
                                                      borderRadius: 0.5,
                                                      bgcolor: 'rgba(255,255,255,0.15)',
                                                      border: '1px solid rgba(255,255,255,0.1)'
                                                    }}
                                                  >
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                                      <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                          opacity: 0.8, 
                                                          fontSize: '0.7rem',
                                                          overflow: 'hidden',
                                                          textOverflow: 'ellipsis',
                                                          whiteSpace: 'nowrap',
                                                          minWidth: 0,
                                                          flex: 1
                                                        }}
                                                      >
                                                        {item.packaging} • {item.volume}г
                                                      </Typography>
                                                      <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                                                        <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                                                          {item.count}×{item.price}₽
                                                        </Typography>
                                                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                                                          {item.totalSum.toLocaleString('ru-RU')}₽
                                                        </Typography>
                                                      </Box>
                                                    </Box>
                                                  </Box>
                                                ))}
                                              </Stack>
                                            </Box>
                                          );
                                        })}
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (

              /* Компактный вид */
              <Grid container spacing={2}>
                {Object.keys(inventory).map(cityName => {
                  const city = inventory[cityName];
                  
                  return Object.keys(city.districts).map(districtName => {
                    const district = city.districts[districtName];
                    const districtItems = Object.values(district.products).reduce((sum, product) => {
                      return sum + product.totalCount;
                    }, 0);
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={`${cityName}-${districtName}`}>
                        <Card 
                          sx={{ 
                            height: '100%',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4
                            }
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <LocationCityIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                  {cityName}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <PlaceIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                  {districtName}
                                </Typography>
                              </Box>
                              <Chip 
                                label={`${districtItems} шт.`}
                                size="small"
                                color="primary"
                                icon={<TrendingUpIcon />}
                              />
                            </Box>
                            
                            <Typography variant="h5" fontWeight="bold" color="primary" mb={2}>
                              {district.totalSum.toLocaleString('ru-RU')} ₽
                            </Typography>
                            
                            <Stack spacing={1}>
                              {Object.keys(district.products).map(productName => {
                                const product = district.products[productName];
                                return (
                                  <Paper 
                                    key={productName}
                                    sx={{ 
                                      p: 1.5, 
                                      bgcolor: 'grey.50',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'grey.200'
                                    }}
                                  >
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1}>
                                      <Typography 
                                        variant="subtitle2" 
                                        fontWeight="medium"
                                        sx={{ 
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          minWidth: 0,
                                          flex: 1
                                        }}
                                      >
                                        {productName}
                                      </Typography>
                                      <Chip 
                                        label={`${product.totalCount} шт.`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ flexShrink: 0 }}
                                      />
                                    </Box>
                                    
                                    <Stack spacing={0.5}>
                                      {product.items.map((item, index) => (
                                        <Box 
                                          key={index}
                                          display="flex" 
                                          justifyContent="space-between" 
                                          alignItems="center"
                                          sx={{ 
                                            p: 0.5,
                                            borderRadius: 0.5,
                                            bgcolor: 'white',
                                            fontSize: '0.75rem'
                                          }}
                                        >
                                          <Typography variant="caption">
                                            {item.packaging} • {item.volume}г
                                          </Typography>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="caption" color="text.secondary">
                                              {item.count}×{item.price}₽
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold">
                                              {item.totalSum.toLocaleString('ru-RU')}₽
                                            </Typography>
                                          </Box>
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  });
                })}
              </Grid>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default Inventory;
